import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';

// Client data layer for file sharing: who can read one of your files, and what other people have
// shared with you.
//
// orion-web's CLIENT bundle must not import common-lib (same rule as lib/workspace-files.ts), so the
// item shapes below are client-local contracts matching what pages/api/file-shares.ts,
// shared-files.ts and shared-file.ts serve. Every call goes through `apiFetch`/`API_BASE`.
//
// The states are the discriminated union the rest of the app uses. `error` is a REAL state here, not
// a styling detail: a file is private exactly when its share list is empty AND the read succeeded,
// so collapsing a failed read into `{ shares: [] }` would render "Private" over an unknown.

/** One grantee. Mirrors `FileShareSummary` in pages/api/file-shares.ts. */
export interface FileShareItem {
  granteeRecipientId: string;
  /** Both optional on the wire — a row with neither is still a real grant. */
  granteeEmail?: string;
  granteeName?: string;
  /** True when this share created the person: nobody answered to that address until now. */
  granteeIsNew?: boolean;
  /** Only ever `false` — absent means accepted. See the route's `toSummary`. */
  accepted?: boolean;
  /** Who added them, when it was not you. Absent on your own grants. */
  grantedByName?: string;
  createdDate?: string;
}

/** One file somebody shared with you. Mirrors `SharedFileSummary` in pages/api/shared-files.ts. */
export interface SharedFileItem {
  fileId: string;
  fileName: string;
  ownerName?: string;
  sharedByName?: string;
  createdDate?: string;
  contentType?: string;
}

export type FileSharesState = { status: 'loading' } | { status: 'ready'; shares: FileShareItem[] } | { status: 'error' };

export type SharedFilesState = { status: 'loading' } | { status: 'ready'; files: SharedFileItem[]; pending: SharedFileItem[] } | { status: 'error' };

export async function fetchFileShares(fileId: string): Promise<FileShareItem[]> {
  const res = await apiFetch(`${API_BASE}/api/file-shares?fileId=${encodeURIComponent(fileId)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    // Throwing is the point: the hook turns this into `{ status: 'error' }`, which the dialog renders
    // as "couldn't load who has access" + retry. Returning [] here would say "Private" instead.
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { shares?: FileShareItem[] };
  return data.shares ?? [];
}

/** What a share attempt answered. Three outcomes, and the middle one wrote NOTHING. */
export interface ShareFileResult {
  share: FileShareItem | null;
  /** Nothing was written. Ask the owner, then retry with `confirmUnknown`. */
  requiresConfirmation: boolean;
  /** Absent when the service did not say — do NOT collapse to false, see the route. */
  granteeNotified?: boolean;
  /**
   * The grant already existed, so nothing was announced on purpose. A plain boolean, unlike
   * `granteeNotified`: it only ever suppresses a line, so false is the safe default.
   */
  alreadyShared: boolean;
  shareLink: string | null;
  /**
   * The id the grant was keyed on — the caller's own when it had one, else the one the server just
   * minted from the path. Null when nothing was written.
   *
   * A file shared by path has no id on this client until this comes back, and the share list is keyed
   * on the id, so without it the dialog reads "Private" about the person it just shared with.
   */
  fileId: string | null;
}

export async function shareFile(fileId: string, filePath: string, granteeEmail: string, confirmUnknown = false): Promise<ShareFileResult> {
  const res = await apiFetch(`${API_BASE}/api/file-shares`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    // BOTH, always. The id names the file exactly and is used whenever present; the path is what
    // lets a file older than the id field be shared at all — the server mints one from it. Sending
    // the path does not make the grant follow a path: the stored grant is keyed on the resolved id.
    body: JSON.stringify({ fileId, filePath, granteeEmail, ...(confirmUnknown ? { confirmUnknown: true } : {}) }),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as {
    share?: FileShareItem | null;
    requiresConfirmation?: boolean;
    granteeNotified?: boolean;
    alreadyShared?: boolean;
    shareLink?: string | null;
    fileId?: string | null;
  };
  return {
    share: data.share ?? null,
    requiresConfirmation: data.requiresConfirmation === true,
    ...(typeof data.granteeNotified === 'boolean' ? { granteeNotified: data.granteeNotified } : {}),
    alreadyShared: data.alreadyShared === true,
    shareLink: data.shareLink ?? null,
    fileId: data.fileId ?? null,
  };
}

export async function revokeFileShare(fileId: string, granteeRecipientId: string): Promise<void> {
  // The two ids ride the query string as well as the body: a DELETE body is not carried by every
  // client or proxy, and losing the grantee id silently would be a revoke aimed at nobody.
  const query = `fileId=${encodeURIComponent(fileId)}&granteeRecipientId=${encodeURIComponent(granteeRecipientId)}`;
  const res = await apiFetch(`${API_BASE}/api/file-shares?${query}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ fileId, granteeRecipientId }),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}

export async function fetchSharedFiles(): Promise<{ files: SharedFileItem[]; pending: SharedFileItem[] }> {
  const res = await apiFetch(`${API_BASE}/api/shared-files`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as { files?: SharedFileItem[]; pending?: SharedFileItem[] };
  return { files: data.files ?? [], pending: data.pending ?? [] };
}

/** The body of one shared file, at the owner's current version. */
export interface SharedFileBody {
  available: boolean;
  fileName?: string;
  content?: string;
  contentType?: string;
  referenceUrl?: string;
  ownerName?: string;
}

export async function fetchSharedFile(fileId: string): Promise<SharedFileBody> {
  const res = await apiFetch(`${API_BASE}/api/shared-file?fileId=${encodeURIComponent(fileId)}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  return (await res.json()) as SharedFileBody;
}

export async function answerFileShare(fileId: string, answer: 'accept' | 'decline'): Promise<void> {
  const res = await apiFetch(`${API_BASE}/api/file-share-answer`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ fileId, answer }),
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
}

export function useFileShares(fileId: string | undefined): FileSharesState {
  const query = useQuery({
    queryKey: queryKeys.fileShares(fileId),
    queryFn: () => fetchFileShares(fileId as string),
    enabled: !!fileId,
  });

  // A file with no id yet cannot be the subject of a grant — every share row keys on one — so nobody
  // can read it. That is a KNOWN answer, not an unfinished read: `ready` with an empty list renders
  // "Private", where waiting on a query that is disabled and will never run spins forever.
  if (!fileId) return { status: 'ready', shares: [] };
  if (query.data) return { status: 'ready', shares: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}

/** Everything shared with you. `error` is a real state — see the note at the top. */
export function useSharedFiles(): SharedFilesState {
  const query = useQuery({
    queryKey: queryKeys.sharedFiles(),
    queryFn: fetchSharedFiles,
  });

  if (query.data) return { status: 'ready', files: query.data.files, pending: query.data.pending };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}
